import { ILocation } from '@monopolio-ui/state-library/stores/filterStore/types'
import { useEffect, useRef, useState } from 'react'
import { MdOutlineClose, MdOutlineSearch } from 'react-icons/md'
import { TiDeleteOutline } from "react-icons/ti";

export interface Props {
  focusInput?: boolean

  /**
   * @description This function is called when the input is focused or blurred
   * @type {boolean}
   * */
  onFocused: (status: boolean) => void

  /**
   * @description This function is called when the input value changes
   * @type {string}
   * */
  onChange: (value: string) => void

  /**
   * @description This is the default value of the input
   * @type {string}
   * */
  defaultValue?: string

  /**
   * @description This is the className of the input
   * @type {string}
   * */
  className?: string

  /**
   * @description This is the icon of the input, define the position of the icon
   * @type {string}
   * */
  alignIcon?: 'left' | 'right'

  /**
   * @description This is de className of the icon
   * @type {string}
   * */
  iconClassName?: string

  /**
   * @description This is the placeholder of the input
   * @type {string}
   * */
  placeholder?: string

  /**
   * @description This is the className of the field
   * @type {string}
   * */
  placeholderClassName?: string

  /**
   * @description This is the className of the value
   * @type {string}
   * */

  /**
   * @description locations to show in the autocomplete
   * @type {ILocation[]}
   */

  locations?: ILocation[]

  /**
   * @description This function is called when the locations delete button is clicked
   * @type {(id:string) => void}
   */

  onDeleteLocation?: (id:string) => void
    
  valueClassName?: string
  iconCleanSearch?: string

  /**
   * @description This function is called when the input is cleared
   * @type {() => void}
   */
  onClear: () => void
  disabled?: boolean
  showPlaceHolderAsPlaceHolder?: boolean
  enableEmptystringForResetInput?: boolean
}

/**
 * This component is a search field for the "autocomplete" component.
 * @param focusInput - Set the focus of the input
 * @param onFocused - This function is called when the input is focused or blurred
 * @param onChange - This function is called when the input value changes
 * @param defaultValue - This is the default value of the input
 * @param className - This is the className of the input
 * @param alignIcon - This is the icon of the input, define the position of the icon
 * @param iconClassName - This is de className of the icon
 * @param placeholder - This is the placeholder of the input
 * @param placeholderClassName - This is the className of the field
 * @param valueClassName - This is the className of the value
 * @param onClear - This function is called when the input is cleared
 * @param enableEmptystringForResetInput - boolean to enable empty string for reset input
 * @param locations - locations to show in the autocomplete
 * @param onDeleteLocation - This function is called when the locations delete button is clicked
 * @returns {JSX.Element}
 */
const SearchField = ({
  focusInput = false,
  onFocused,
  onChange,
  defaultValue = '',
  className,
  alignIcon = 'right',
  iconClassName,
  placeholder = 'Ubicación, lugar, amenidades, tipo de inmueble',
  placeholderClassName,
  valueClassName,
  onClear,
  disabled,
  iconCleanSearch = '',
  showPlaceHolderAsPlaceHolder,
  enableEmptystringForResetInput = false,
  onDeleteLocation,
  locations
}: Props): JSX.Element => {
  const ref = useRef<any>(null)

  const firtsLocation = locations?.[0]
  const [isFocused, setIsFocused] = useState(false)

  const [searchText, setSearchText] = useState<string>(defaultValue || '')

  /**
   * This function is callled change the value of the input
   * @return {void}
   * */
  const update = (event: any): void => {
    const value: string = event.target.value
    const clean = value?.trimStart()
    setSearchText(clean)
  }

  /**
   * This function is called to clear the input
   * @return {void}
   */
  const clear = (): void => {
    setSearchText('')
    setIsFocused(true)
    onClear()
  }

  /**
   * This function is responsible for handling the keydown event.
   * @param event - This is the event to handle.
   * @returns {Promise<void>}
   */
  const handleKeyDown = async (event: any): Promise<void> => {
    if (event.key === 'Enter') {
      ref?.current?.blur()
    }
  }

  useEffect(() => {
    if (focusInput) {
      ref?.current?.focus()
    }
    setIsFocused(focusInput)
  }, [focusInput])

  useEffect(() => {
    onFocused(isFocused)
  }, [isFocused])

  useEffect(() => {
    if ((!defaultValue || defaultValue === searchText) && defaultValue !== '') {
      return
    }

    if (defaultValue === '' && !enableEmptystringForResetInput) {
      return
    }

    setSearchText(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    onChange(searchText)
  }, [searchText])

  return (
    <div
      className={`relative h-[42px] w-full flex gap-2 items-center  rounded-full z-0 ${className}`}
    >
      {disabled && (
        <div className="absolute right-0 top-0 w-full h-full bg-white opacity-50 z-[1] rounded-full" />
      )}
      {alignIcon === 'left' ? (
        <MdOutlineSearch
          className={` w-6 h-6 text-text-primary ${iconClassName}`}
        />
      ) : null}

      <div className="relative flex items-center w-full">
        {
          firtsLocation && (
            <div className="flex items-center px-2 py-1 rounded-full bg-[#F4F7FA] gap-1 w-[105px]">
              <p className='max-w-[69px] truncate text-[10px] font-bold uppercase'>{firtsLocation.searchAddress}</p>
              <button type='button' onClick={() => onDeleteLocation?.(`${firtsLocation.latitude}${firtsLocation.longitude}`)} className="w-4 h-4 text-text-primary">
                <TiDeleteOutline className='text-base' />
              </button>
            </div>
          )
        }
        {
          locations && locations?.length > 1 && (
            <div className="flex items-center px-2 py-1 rounded-full bg-[#F4F7FA] gap-1 w-fit">
              <p className='max-w-[69px] truncate text-[10px] font-bold uppercase'>{(locations?.length-1)} más</p>
            </div>
          )
        }
        <p
          className={`absolute w-full truncate font-medium text-text-primary duration-150 ${
            isFocused || searchText || firtsLocation ? 'top-[1px] text-xs' : 'text-base'
          }
          ${
            (isFocused || searchText) && showPlaceHolderAsPlaceHolder
              ? '!hidden'
              : 'block'
          }
           ${placeholderClassName}
           `}
        >
          {placeholder}
        </p>
        <input
          disabled={disabled}
          ref={ref}
          type="text"
          className={`w-full h-[inherit] bg-transparent relative outline-none ${
            showPlaceHolderAsPlaceHolder ? '!pt-0' : 'pt-4'
          } text-text-primary truncate
           ${valueClassName}
           `}
          value={searchText}
          onChange={update}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>

      {searchText ? (
        <MdOutlineClose
          onClick={clear}
          className={`w-6 h-6 text-text-primary cursor-pointer ${iconCleanSearch}`}
        />
      ) : null}

      {alignIcon === 'right' && !searchText ? (
        <MdOutlineSearch
          className={`w-6 h-6 text-text-primary ${iconClassName}`}
        />
      ) : null}
    </div>
  )
}

export default SearchField
